import { useCallback } from 'react'
import { heapEvent } from '@equipmentshare/heaper'
import { useCreateProductListMutation, ProductListTargetType } from '../../../graphql'
import { getCostCapturePurchaseOrderUrl, getDemandRequestUrl } from '../../../config/host'

type LineItem = {
	lineItemId: string,
	productId: string,
	quantityRequested: number,
	targetType?: string | null,
	targetId?: string | null,
  lineNotes?: string | null,
}

type Inventory = {
	id: string,
	branchId: string,
}

type DemandRequest = {
	id: string,
	requestNumber: number,
	lineItems: LineItem[],
	requestingInventory: Inventory,
  notes?: string | null,
}

type UsePurchaseOrderCreationParams = {
	request: DemandRequest | undefined | null,
	selectedLineItemIds: string[],
	addToast: ( message: string ) => void,
}

/**
 * Hook for creating purchase orders from selected line items
 */
export function usePurchaseOrderCreation( {
	request,
	selectedLineItemIds,
	addToast,
}: UsePurchaseOrderCreationParams ) {
	const [ createProductList ] = useCreateProductListMutation()

	const handleCreatePurchaseOrder = useCallback( async() => {
		if ( !request || selectedLineItemIds.length === 0 ) {
			return
		}

		try {
			// Map IDs → items in selectedLineItemIds order (display order) rather than
			// filtering request.lineItems, which would revert to server insertion order.
			const selectedLineItems = selectedLineItemIds
				.map( ( id ) => request.lineItems.find( ( item ) => item.lineItemId === id ) )
				.filter( ( item ): item is LineItem => item !== undefined )

			// Create product list items from selected line items
			// Note: DemandRequestTargetType and ProductListTargetType have identical enum values
			const listItems = selectedLineItems.map( ( lineItem ) => ( {
				productId: lineItem.productId,
				quantity: lineItem.quantityRequested,
				targetType: lineItem.targetType as ProductListTargetType | null | undefined,
				targetId: lineItem.targetId,
				notes: lineItem.lineNotes,
				context: {
					demandRequestLineItemId: lineItem.lineItemId,
				},
			} ) )

			// Create product list
			const result = await createProductList( {
				variables: {
					input: {
						items: listItems,
						context: {
							fromEntityId: request.id,
							fromEntityType: 'INVENTORY.DEMAND_REQUEST',
							fromEntityName: `REQ-${request.requestNumber}`,
							branchId: request.requestingInventory.branchId,
							fromUrl: getDemandRequestUrl( request.id ),
							inventoryId: request.requestingInventory.id,
							notes: request.notes
						},
					},
				},
			} )

			const createProductListResult = result.data?.createProductList

			if ( createProductListResult?.success && createProductListResult.productList?.id ) {
				const listId = createProductListResult.productList.id
				const poUrl = getCostCapturePurchaseOrderUrl( listId )

				heapEvent( 'Inventory - Bulk Actions - Create PO - Product List Created', {
					demand_request_id: request.id,
					list_id: listId,
					item_count: selectedLineItemIds.length,
				} )

				window.open( poUrl, '_blank' )
				addToast( 'Product list created and opened in Purchase Orders app' )
			} else {
				const errorMessage = createProductListResult?.message ?? 'Unknown error occurred'
				addToast( `Failed to create product list: ${errorMessage}` )
			}
		} catch ( err ) {
			addToast( `Failed to create product list: ${err instanceof Error ? err.message : 'Unknown error'}` )
		}
	}, [ request, selectedLineItemIds, createProductList, addToast ] )

	return {
		handleCreatePurchaseOrder,
	}
}
